import React from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import type { AllocationType, PettyCashCategory } from '../../types/pettyCash';
import { categoryDetail, categoryMatches } from '../../utils/pettyCash';

/**
 * A receipt's expense category: searchable by name, account or auxiliary. Each choice shows the account and the
 * auxiliary (SAP Dimension 5) it gives this claim's side, project or department (Petty Cash Setup › Categories).
 */

interface Props {
  id: string;
  label: string;
  categories: PettyCashCategory[];
  value: number | null;
  allocationType: AllocationType;
  onChange: (categoryId: number | null) => void;
  error?: boolean;
  helperText?: React.ReactNode;
  required?: boolean;
}

const CategoryPicker: React.FC<Props> = ({ id, label, categories, value, allocationType, onChange, error, helperText, required }) => {
  const selected = categories.find((c) => c.categoryId === value) || null;
  return (
    <Autocomplete
      id={id}
      options={categories}
      value={selected}
      onChange={(_e, c) => onChange(c ? c.categoryId : null)}
      getOptionLabel={(c) => c.name}
      isOptionEqualToValue={(a, b) => a.categoryId === b.categoryId}
      filterOptions={(options, state) => options.filter((c) => categoryMatches(c, allocationType, state.inputValue))}
      autoHighlight
      noOptionsText="No category matches: try part of a name, an account or an auxiliary"
      renderOption={(props, c) => (
        <li {...props} key={c.categoryId}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2">{c.name}</Typography>
            <Typography variant="caption" color="text.secondary" component="div">{categoryDetail(c, allocationType)}</Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          placeholder="Type to search"
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
        />
      )}
    />
  );
};

export default CategoryPicker;
